import { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { clientesApi } from "src/services/clientesApi";
import { dbx } from "src/utils/urlBack_End";

const UPLOAD_FILE_SIZE_LIMIT = 150 * 1024 * 1024; // 150MB

export const useFileUpload = (clienteRut) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [uploading, setUploading] = useState(null);

  const { sucursal } = useSelector((state) => state.auth.user);

  // RTK Query mutation para agregar archivo
  const [agregarArchivoMutation] =
    clientesApi.useAgregarArchivoPortafolioMutation();

  // Seleccionar archivo
  const handleFileSelect = useCallback((event) => {
    const file = event.target.files[0];

    if (!file) {
      setSelectedFile(null);
      return;
    }

    // Validar tamaño
    if (file.size >= UPLOAD_FILE_SIZE_LIMIT) {
      setUploadError(
        `El archivo es demasiado grande. Máximo ${
          UPLOAD_FILE_SIZE_LIMIT / (1024 * 1024)
        }MB`,
      );
      return;
    }

    setSelectedFile(file);
    setUploadError(null);
  }, []);

  // Remover archivo seleccionado
  const clearSelectedFile = useCallback(() => {
    setSelectedFile(null);
    setUploadError(null);
  }, []);

  // Subir archivo
  const uploadFile = useCallback(
    async (portafolioId) => {
      if (!selectedFile || !portafolioId || !clienteRut) {
        setUploadError("Faltan datos requeridos para la subida");
        return { success: false };
      }

      setUploadError(null);

      setUploading(true);
      try {
        // Subir a Dropbox
        await dbx.filesUpload({
          path: `/Clientes/${clienteRut}/Portafolio/${selectedFile.name}`,
          contents: selectedFile,
          mode: "overwrite",
        });

        // Obtener URL compartida
        let shareUrl;
        try {
          const shareResponse = await dbx.sharingCreateSharedLinkWithSettings({
            path: `/Clientes/${clienteRut}/Portafolio/${selectedFile.name}`,
          });
          shareUrl = shareResponse.result.url;
        } catch (shareError) {
          // Si ya existe el link compartido
          if (shareError.error?.error?.shared_link_already_exists) {
            shareUrl =
              shareError.error.error.shared_link_already_exists.metadata.url;
          } else {
            throw shareError;
          }
        }

        // Guardar en BD usando RTK Query
        await agregarArchivoMutation({
          refPortafolioCliente: portafolioId,
          urlArchivo: shareUrl,
          nombreArchivo: selectedFile.name,
          refSucursal: sucursal?.refSucursal,
        }).unwrap();

        // Limpiar estado
        setSelectedFile(null);
        return { success: true };
      } catch (error) {
        console.error("Error uploading file:", error);
        setUploadError(
          error.data?.message ||
            "Error al subir el archivo. Intenta nuevamente.",
        );
        return {
          success: false,
          error: error.data?.message || error.message,
        };
      } finally {
        setUploading(false);
      }
    },
    [selectedFile, clienteRut, sucursal, agregarArchivoMutation],
  );

  return {
    selectedFile,
    uploading,
    uploadError,
    handleFileSelect,
    clearSelectedFile,
    uploadFile,
  };
};
